import { DeleteConfirm } from '@/components/admin/reglages/reference-list';
import { ambianceFileUrl } from '@/lib/api';
import {
  ASSET_KIND_LABEL,
  assetDimensionsOrDuration,
  referentLine,
} from '@/lib/mediatheque-labels';
import type { AmbianceAsset, AmbianceAssetReferent } from '@matter_extractor/shared';
import { Button } from '@pms-digital/design-system';
import { Link } from '@tanstack/react-router';
import {
  MEDIATHEQUE_CARD_TESTID,
  MEDIATHEQUE_DELETE_CONFIRM_TESTID,
  MEDIATHEQUE_DELETE_TESTID,
  MEDIATHEQUE_IN_USE_TESTID,
} from './contracts';

/**
 * One file of the Médiathèque (§16 of the issue #121 grill): a thumbnail or
 * an `<audio controls>`, its facts, and the delete gesture - inline, at the
 * régime of `reglages/reference-list.tsx`'s `DeleteConfirm`, never a
 * full-screen modal (`versioned-dialog` is for the versioned layer's three
 * boxes, and a file is not a versioned object).
 *
 * The three states of the card's foot are mutually exclusive and driven by
 * the PARENT (issue #121, §9.1): at rest, a lone « Supprimer » ; pressed, the
 * inline confirmation ; refused with `ASSET_IN_USE`, the list of referents
 * and no delete control at all - the screen names who cites the file instead
 * of asking a second time.
 */

export interface MediathequeCardProps {
  asset: AmbianceAsset;
  /** The inline confirmation is open for THIS card. */
  confirming: boolean;
  /** The delete request for THIS card is in flight. */
  deleting: boolean;
  /**
   * A DIFFERENT card's delete is in flight - this one's "Supprimer" closes
   * rather than let an operator start a second delete on the shared mutation
   * while the first is still running (issue #121 review).
   */
  disableDelete: boolean;
  /** Non-null once the server refused THIS card's deletion with its referents. */
  referents: readonly AmbianceAssetReferent[] | null;
  onRequestDelete: () => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  onDismissReferents: () => void;
}

export function MediathequeCard({
  asset,
  confirming,
  deleting,
  disableDelete,
  referents,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  onDismissReferents,
}: MediathequeCardProps) {
  const dims = assetDimensionsOrDuration(asset);
  return (
    <li
      data-testid={MEDIATHEQUE_CARD_TESTID}
      className="flex flex-col overflow-hidden rounded-md border-2 border-gray-200 bg-white"
    >
      {asset.kind === 'image' ? (
        <img
          src={ambianceFileUrl(asset.id)}
          alt={asset.filename}
          loading="lazy"
          className="aspect-[16/10] w-full bg-gray-50 object-cover"
        />
      ) : (
        <div className="flex items-center justify-center bg-gray-50 p-4">
          <audio
            controls
            preload="none"
            src={ambianceFileUrl(asset.id)}
            className="w-full"
            aria-label={`Écouter ${asset.filename}`}
          >
            <track kind="captions" />
          </audio>
        </div>
      )}

      <div className="flex flex-1 flex-col gap-2 p-3">
        <div className="space-y-0.5">
          {/* Truncated without a native `title` - the design system forbids the
              browser-drawn bubble (issue #103); `media-picker.tsx`'s tiles
              truncate the same way with none. */}
          <p className="truncate font-semibold text-gray-800 text-small">{asset.filename}</p>
          <div className="flex flex-wrap items-center gap-1.5 text-gray-600 text-caption">
            <span className="rounded-full border-2 border-bleu-nuit px-2 py-0.5 font-semibold text-bleu-nuit">
              {ASSET_KIND_LABEL[asset.kind]}
            </span>
            {dims === null ? null : <span>{dims}</span>}
          </div>
        </div>

        {asset.license === '' ? null : (
          <p className="text-gray-600 text-caption">
            <span className="font-semibold">Licence : </span>
            {asset.license}
          </p>
        )}

        {asset.tags.length === 0 ? null : (
          <ul className="flex flex-wrap gap-1">
            {asset.tags.map((tag) => (
              <li
                key={tag}
                className="rounded-full border border-gray-300 bg-gray-100 px-2 py-0.5 text-gray-700 text-caption"
              >
                {tag}
              </li>
            ))}
          </ul>
        )}

        <div className="mt-auto pt-1">
          {referents !== null ? (
            <div
              role="alert"
              data-testid={MEDIATHEQUE_IN_USE_TESTID}
              className="space-y-2 rounded-md border-2 border-rouge bg-rouge-tint p-3"
            >
              <p className="text-gray-800 text-small leading-5">
                Ce fichier est cité par une version taguée d’une expérience : remplacez-le avant de
                le supprimer.
              </p>
              <ul className="space-y-1">
                {referents.map((referent) => (
                  <li key={referent.id} className="text-small">
                    <Link
                      to="/admin/experiences/$experienceId"
                      params={{ experienceId: referent.id }}
                      className="text-bleu-nuit underline hover:no-underline"
                    >
                      {referentLine(referent)}
                    </Link>
                  </li>
                ))}
              </ul>
              <Button type="button" variant="ghost" size="sm" onClick={onDismissReferents}>
                Fermer
              </Button>
            </div>
          ) : confirming ? (
            <DeleteConfirm
              testId={MEDIATHEQUE_DELETE_CONFIRM_TESTID}
              label={`Supprimer ${asset.filename}`}
              busy={deleting}
              onCancel={onCancelDelete}
              onConfirm={onConfirmDelete}
            >
              Supprimer « {asset.filename} » ? Cette suppression est définitive - les octets sont
              effacés du magasin.
            </DeleteConfirm>
          ) : (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={disableDelete}
              data-testid={MEDIATHEQUE_DELETE_TESTID}
              onClick={onRequestDelete}
            >
              Supprimer
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
